<template>
  <div v-if="documentFiles.length > 1" class="document-file-switcher">
    <span class="document-file-switcher__label">
      {{ i18n.t('labels.document') }}
      {{ currentDocumentNumber }}
      {{ i18n.t('labels.of') }}
      {{ totalDocumentCount }}
    </span>
    <div class="document-file-switcher__actions">
      <v-btn
        aria-label="Previous document"
        class="document-file-switcher__button"
        :class="{
          'document-file-switcher__button--disabled': isPreviousDisabled
        }"
        :disabled="isPreviousDisabled"
        icon="fal fa-chevron-left"
        size="small"
        variant="flat"
        @click="goToPrevious"
      />
      <v-btn
        aria-label="Next document"
        class="document-file-switcher__button"
        :class="{ 'document-file-switcher__button--disabled': isNextDisabled }"
        :disabled="isNextDisabled"
        icon="fal fa-chevron-right"
        size="small"
        variant="flat"
        @click="goToNext"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from '@/stores/global.ts';
import type { Document, DocumentFile } from '@/types/Document.ts';
import type { PropType } from 'vue';

const props = defineProps({
  document: {
    type: Object as PropType<Document | null | undefined>,
    default: null
  }
});

const i18n = useI18n();
const globalStore = useGlobalStore();

const documentFiles = computed<DocumentFile[]>(() => {
  const files = props.document?.files || [];

  if (files.length > 0) return files;

  if (!props.document?.documentName || !props.document.documentUrl) return [];

  return [
    {
      fileId: props.document.documentUrl,
      fileName: props.document.documentName,
      mimeType: props.document.documentMimeType,
      fileUrl: props.document.documentUrl,
      thumbnailUrl: props.document.thumbnailUrl || '',
      creationTime: props.document.creationTime || '',
      filePolicy: {
        canDelete: false
      }
    }
  ];
});

const currentDocumentIndex = computed(() => {
  const selectedIndex = documentFiles.value.findIndex(
    (file) => file.fileId === globalStore.selectedDocumentFileId
  );

  return selectedIndex >= 0 ? selectedIndex : 0;
});

const currentDocumentNumber = computed(() => currentDocumentIndex.value + 1);
const totalDocumentCount = computed(() => documentFiles.value.length);
const isPreviousDisabled = computed(() => currentDocumentIndex.value <= 0);
const isNextDisabled = computed(
  () => currentDocumentIndex.value >= documentFiles.value.length - 1
);

function setSelectedFileByIndex(index: number) {
  const nextFile = documentFiles.value[index];
  if (!nextFile) return;
  globalStore.setSelectedDocumentFileId(nextFile.fileId);
}

function goToPrevious() {
  if (isPreviousDisabled.value) return;
  setSelectedFileByIndex(currentDocumentIndex.value - 1);
}

function goToNext() {
  if (isNextDisabled.value) return;
  setSelectedFileByIndex(currentDocumentIndex.value + 1);
}

watch(
  documentFiles,
  (files) => {
    if (files.length === 0) {
      globalStore.setSelectedDocumentFileId(null);
      return;
    }

    const selectedFileStillExists = files.some(
      (file) => file.fileId === globalStore.selectedDocumentFileId
    );

    if (!selectedFileStillExists) {
      globalStore.setSelectedDocumentFileId(files[0].fileId);
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.document-file-switcher {
  display: flex;
  align-items: center;
  gap: 12px;
}

.document-file-switcher__label {
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.document-file-switcher__actions {
  display: flex;
  gap: 8px;
}

.document-file-switcher__button {
  border-radius: 4px;
  min-width: 28px;
  width: 28px;
  height: 28px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.document-file-switcher__button--disabled {
  background: #6b6d72;
  color: rgba(255, 255, 255, 0.8);
}
</style>
